import moment from 'moment-timezone'
import { operatorTypes } from 'core/table/interfaces/Filters'

export type DateFilterBetweenOperand = {
  from: string
  to: string
}

export const toDateFilterStorageValue = (date: Date | null): string => (date ? moment(date).format('YYYY-MM-DD') : '')

export const parseDateFilterStorageValue = (value: unknown): Date | null => {
  if (value == null || value === '') {
    return null
  }
  const parsed = moment(String(value), ['YYYY-MM-DD', moment.ISO_8601], true)
  return parsed.isValid() ? parsed.toDate() : null
}

export const parseDateFilterBetweenOperand = (operand: unknown): DateFilterBetweenOperand => {
  if (operand != null && typeof operand === 'object') {
    const { from, to } = operand as DateFilterBetweenOperand
    return { from: from ?? '', to: to ?? '' }
  }
  return { from: '', to: '' }
}

export const isDateFilterOperator = (operator: string) =>
  [
    operatorTypes.IS_ON,
    operatorTypes.IS_BEFORE,
    operatorTypes.IS_AFTER,
    operatorTypes.IS_BETWEEN,
    operatorTypes.IS_KNOWN,
    operatorTypes.IS_UNKNOWN,
  ].includes(operator as operatorTypes)

export const isDateFilterRowComplete = (operator: string, operand: unknown): boolean => {
  if (operator === operatorTypes.IS_KNOWN || operator === operatorTypes.IS_UNKNOWN) {
    return true
  }
  if (operator === operatorTypes.IS_BETWEEN) {
    const { from, to } = parseDateFilterBetweenOperand(operand)
    return Boolean(from && to)
  }
  return typeof operand === 'string' ? operand.trim() !== '' : operand != null && operand !== ''
}

export const formatDateFilterOperandForDisplay = (operator: string, operand: unknown): string => {
  if (operator === operatorTypes.IS_KNOWN || operator === operatorTypes.IS_UNKNOWN) {
    return ''
  }
  if (operator === operatorTypes.IS_BETWEEN) {
    const { from, to } = parseDateFilterBetweenOperand(operand)
    if (!from || !to) {
      return ''
    }
    const fromDisplay = moment(from, 'YYYY-MM-DD', true).format('MM/DD/YYYY')
    const toDisplay = moment(to, 'YYYY-MM-DD', true).format('MM/DD/YYYY')
    return `${fromDisplay} to ${toDisplay}`
  }
  const date = parseDateFilterStorageValue(operand)
  return date ? moment(date).format('MM/DD/YYYY') : String(operand ?? '')
}

const normalizeCalendarDay = (value: string): string | null => {
  const parsed = moment(value, 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

export const getDateFilterCalendarDay = (rowValue: unknown, _columnId: string, timeZone?: string): string | null => {
  if (rowValue == null || rowValue === '') {
    return null
  }
  if (timeZone) {
    const inOrgTz = moment.tz(rowValue, timeZone)
    return inOrgTz.isValid() ? inOrgTz.format('YYYY-MM-DD') : null
  }
  const parsed = moment(rowValue)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

export const getFilterOperandCalendarDay = (operand: unknown): string | null => {
  if (operand == null || operand === '') {
    return null
  }
  return normalizeCalendarDay(String(operand))
}

export const matchesDateColumnFilter = (
  rowValue: unknown,
  operator: string,
  operand: unknown,
  columnId: string,
  timeZone?: string,
): boolean => {
  const rowDay = getDateFilterCalendarDay(rowValue, columnId, timeZone)

  switch (operator) {
    case operatorTypes.IS_KNOWN:
      return rowDay != null
    case operatorTypes.IS_UNKNOWN:
      return rowDay == null
    case operatorTypes.IS_ON: {
      const filterDay = getFilterOperandCalendarDay(operand)
      return rowDay != null && filterDay != null && rowDay === filterDay
    }
    case operatorTypes.IS_BEFORE: {
      const filterDay = getFilterOperandCalendarDay(operand)
      return rowDay != null && filterDay != null && rowDay < filterDay
    }
    case operatorTypes.IS_AFTER: {
      const filterDay = getFilterOperandCalendarDay(operand)
      return rowDay != null && filterDay != null && rowDay > filterDay
    }
    case operatorTypes.IS_BETWEEN: {
      const { from, to } = parseDateFilterBetweenOperand(operand)
      const fromDay = getFilterOperandCalendarDay(from)
      const toDay = getFilterOperandCalendarDay(to)
      return rowDay != null && fromDay != null && toDay != null && rowDay >= fromDay && rowDay <= toDay
    }
    default:
      return true
  }
}
